import React, { useEffect, useState, useMemo, useCallback } from "react";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import crudsService from "services/cruds-service";
import JobFormTesting from "../jobForm-testing";
import { Box, Grid, IconButton } from "@mui/material";
import AdvancedFilter from "../advancedFilter";
import Footer from "examples/Footer";
import MDAlert from "components/MDAlert";
import MDTypography from "components/MDTypography";
import VirtualizedTable from "examples/Tables/VirtualizedTable";
import { useWebSocket } from "context/WebSocketContext";
import JsonTree from "../modal/jsontree";
import SocketLogs from "../socket_logs";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MDButton from "components/MDButton";
import BatchLogs from "../batchLogs";

const Batch = () => {
  const {socket} = useWebSocket();
  const [filterData, setFilterData] = useState([]);
  const [selectedCompanyRows, setSelectedCompanyRows] = useState([]);
  const [filterTrigger, setFilterTrigger] = useState(false);
  const [notification, setNotification] = useState({ value: false, text: "" });
  const [loader, setLoader] = useState(false);
  const [filterValue, setFilterValue] = useState({});
  const [socketLogs, setSocketLogs] = useState([]);
  const [jobTypeId, setJobTypeId] = useState([]);
  const [completedJobs, setCompletedJobs] = useState({});
  const [jobStatus, setJobStatus] = useState(false);
  const [jsonTreeData, setJsonTreeData] = useState({
    open: false,
    content: null,
    isApiCall: false,
  });
  const [progress, setProgress] = useState({});
  const [selectedName, setSelectedName] = useState("");
  const [viewMode, setViewMode] = useState("table");
  const [currentBatchData, setCurrentBatchData] = useState(null);
  const [selectedAll, setSelectedAll] = useState(false);

  useEffect(() => {
    if (socket) {
      const handleSocketMessage = (event) => {
        let parsedEvent = JSON.parse(event.data);

        if (parsedEvent.event === "BATCH_STATUS") {
          setProgress(parsedEvent?.status);
          setCurrentBatchData(parsedEvent?.status);
        }

        if (
          parsedEvent.event === "BATCH_STATUS" &&
          parsedEvent.status.status === "COMPLETED"
        ) {
          setJobStatus(false);
        }

        setSocketLogs((prevLogs) => [parsedEvent, ...prevLogs]);

        if (parsedEvent.stage === "JOB_DONE_STAGE") {
          setCompletedJobs((prevCompletedJobs) => ({
            ...prevCompletedJobs,
            [parsedEvent.campaign]:
              (prevCompletedJobs[parsedEvent.campaign] || 0) + 1,
          }));
        }
      };

      socket.addEventListener("message", handleSocketMessage);

      return () => {
        socket.removeEventListener("message", handleSocketMessage);
      };
    }
  }, [socket]);

  useEffect(() => {
    setFilterData((prevFilterData) =>
      prevFilterData.map((row) => ({
        ...row,
        process: `${completedJobs[row.uuid] || 0}/${jobTypeId.length}`,
      }))
    );
  }, [completedJobs, jobTypeId]);

  const clearFilter = useCallback((options) => {
    setFilterData([]);
    !options?.preserveSelection && setSelectedCompanyRows([]);
    setFilterTrigger(false);
  }, []);

  const advancedFilter = useCallback(
    async (value, options, isChangeSelectAll = true) => {
      clearFilter(options);
      try {
        setLoader(true);
        setFilterValue(value);
        const response = await crudsService.batchCampaigns(value);
        // const response = await crudsService.advancedFilter(value, "job");
        if (!response.success) throw new Error(response.msg);

        setFilterTrigger(true);
        setFilterData(
          response.data.map((row) => ({
            ...row,
            process: `${completedJobs[row.uuid] || 0}/${jobTypeId.length}`,
          }))
        );
      } catch (error) {
        setNotification({ value: true, text: error.msg ?? error.message });
      } finally {
        setLoader(false);
        if (isChangeSelectAll) {
          setSelectedAll(false);
        }
      }
    },
    [completedJobs, clearFilter]
  );

  const openCompanyDetailModel = async (event, uuid) => {
    event.stopPropagation();
    setJsonTreeData({ open: true, content: uuid, isApiCall: true });
  };

  const getCompanyRows = useMemo(() => {
    return filterData.map((row) => ({
      id: row.company_id,
      uuid: row.uuid,
      campaign_id: row.campaign_id,
      name: (
        <a
          onClick={(e) => {
            e.stopPropagation();
            if (selectedName == row.CampaignCompany?.company_name) {
              setSelectedName("");
            } else {
              setSelectedName(row.CampaignCompany?.company_name);
            }
          }}
        >
          {row.CampaignCompany?.company_name}
        </a>
      ),
      process: row.process,
      action: (
        <IconButton onClick={(e) => openCompanyDetailModel(e, row.company_id)}>
          <MDTypography>
            <VisibilityIcon />
          </MDTypography>
        </IconButton>
      ),
    }));
  }, [filterData]);

  const tableDeviceAllData = useMemo(
    () => ({
      columns: [
        { Header: "ID", accessor: "id", width: 100, notShrink: true },
        { Header: "uuid", accessor: "uuid", width: 280, notShrink: true },
        {
          Header: "Campaign Id",
          accessor: "campaign_id",
          width: 120,
          notShrink: true,
        },
        { Header: "Company Name", accessor: "name", width: 320, grow: true },
        { Header: "Process", accessor: "process", width: 110, notShrink: true },
        {
          Header: "Action",
          accessor: "action",
          width: 110,
          notShrink: true,
        },
      ],
      rows: getCompanyRows,
    }),
    [getCompanyRows]
  );

  const handleJobTypeSelect = useCallback(
    (selectedJobTypeId) => {
      setJobTypeId(selectedJobTypeId);
      setCompletedJobs({});

      const updatedFilterData = filterData.map((row) => ({
        ...row,
        process: `${completedJobs[row.uuid] || 0}/${selectedJobTypeId.length}`,
      }));

      setFilterData(updatedFilterData);
    },
    [filterData, completedJobs]
  );

  const handleViewData = useCallback((data) => {
    setJsonTreeData({ open: true, content: data, isApiCall: false });
  }, []);
  const handleCloseData = useCallback(() => {
    setJsonTreeData({ open: false, content: null, isApiCall: false });
  }, []);

  const searchFun = useCallback(
    (options) => advancedFilter(filterValue, options, false),
    [filterValue, advancedFilter]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {notification.value && (
        <MDAlert color="info" my="20px">
          <MDTypography variant="body2" color="white">
            {notification.text}
          </MDTypography>
        </MDAlert>
      )}

      <MDBox>
        <MDBox
          display="flex"
          flexDirection="column"
          alignItems="start"
          justifyContent="center"
        >
          <JobFormTesting
            selectedCompanyRows={selectedCompanyRows}
            filterData={filterData}
            onJobTypeSelect={handleJobTypeSelect}
            setJobTypeId={setJobTypeId}
            onSocketDataRemove={setSocketLogs}
            onProgressReset={setProgress}
            onCompletedJobDataRemove={setCompletedJobs}
            jobStatus={jobStatus}
            setJobStatus={setJobStatus}
            isBatch={true}
          >
            <Grid item>
              <AdvancedFilter
                setFilter={advancedFilter}
                clearFilter={clearFilter}
                componentName="jobqueue"
              />
            </Grid>
            <Grid item>
              <MDButton
                variant="contained"
                onClick={() =>
                  setViewMode((prev) => (prev === "table" ? "batch" : "table"))
                }
              >
                {viewMode === "table" ? "Switch to Batch" : "Switch to Table"}
              </MDButton>
            </Grid>
          </JobFormTesting>
        </MDBox>
        <Box sx={{ flexGrow: 1, mt: 2 }}>
          <Grid container spacing={2}>
            {/* Show BatchLogs by default with full width */}
            {!filterTrigger && viewMode === "batch" && (
              <Grid item xs={12}>
                <BatchLogs currentBatchData={currentBatchData} />
              </Grid>
            )}

            {/* Show the table and BatchLogs side by side after filterTrigger is true */}
            {(filterTrigger || loader) && (
              <>
                <Grid item xs={12} md={7} lg={8}>
                  {viewMode === "table" ? (
                    <VirtualizedTable
                      table={tableDeviceAllData}
                      loader={loader}
                      selectedRows={selectedCompanyRows}
                      setSelectedRows={setSelectedCompanyRows}
                      isSelection={true}
                      canSearch={true}
                      canRefresh={true}
                      canShowRecords={true}
                      searchFun={searchFun}
                      selectedAll={selectedAll}
                      setSelectedAll={setSelectedAll}
                    />
                  ) : (
                    <BatchLogs currentBatchData={currentBatchData} />
                  )}
                </Grid>

                <Grid item xs={12} md={5} lg={4}>
                  <SocketLogs
                    socketLogs={socketLogs}
                    handleViewData={handleViewData}
                    progress={progress}
                    selectedName={selectedName}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </MDBox>

      {jsonTreeData.open && (
        <JsonTree
          open={jsonTreeData.open}
          onClose={handleCloseData}
          data={jsonTreeData.content}
          isApiCall={jsonTreeData.isApiCall}
        />
      )}

      <Footer />
    </DashboardLayout>
  );
};

export default Batch;
